import styled from "@emotion/styled";
import React, { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { Element } from "react-markdown/src/ast-to-react";

type Props = ReactMarkdown.ReactMarkdownOptions;

export const Markdown = (props: Props) => (
  <ReactMarkdown
    components={{
      a: MarkdownLink,
      code: MarkdownCode,
    }}
    {...props}
  />
);

const MarkdownLink = (props: { children: React.ReactNode }) => (
  <a {...props} target="_blank" rel="noopener noreferrer">
    {props.children}
  </a>
);

const MarkdownCode = (props: {
  children: React.ReactNode;
  inline?: boolean;
  node: Element;
}) => {
  if (props.inline === true) return <code>{props.children}</code>;

  const codeText = props.children ? (props.children as string[])[0] : "";
  return <CanvasCode code={codeText} />;
};

function CanvasCode(props: { code: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (canvasRef.current) {
      const lineHeight = 18;
      const fontStyle = "14px 'Roboto Mono', monospace";
      const linesOfText = props.code.replace(/\n+$/, "").split("\n");

      const { current: canvas } = canvasRef;
      const ctx = canvas.getContext("2d")!;
      ctx.font = fontStyle;

      const canvasWidth = Math.floor(
        Math.max(...linesOfText.map((line) => ctx.measureText(line).width)),
      );
      const canvasHeight = Math.floor(linesOfText.length * lineHeight + 5);

      const scale = window.devicePixelRatio;
      canvas.width = Math.floor(canvasWidth * scale);
      canvas.height = Math.floor(canvasHeight * scale);

      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;

      ctx.scale(scale, scale);

      /*
      TODO:
      font is set second time, because changing canvas dimentions
      (calculated by its context) resets previously set font
      */
      ctx.font = fontStyle;

      linesOfText.forEach((line, indx) => {
        ctx.fillText(line, 0, (indx + 1) * lineHeight);
      });
    }
  }, [props.code, canvasRef]);

  return (
    <CodeCanvasContainer>
      <canvas ref={canvasRef} />
    </CodeCanvasContainer>
  );
}

const CodeCanvasContainer = styled.div`
  background-color: ${(props) => props.theme.colors.purple[5]};
  padding: 15px 20px;
  overflow-x: auto;
  border-radius: 8px;
`;
